/** What a prefix search needs to know about an option. */
export interface SearchableOption {
    readonly label: string;
    readonly disabled?: boolean;
}

/**
 * Folds a label into something comparable: lower case, and umlauts reduced to their
 * base letter, so typing `a` also reaches `Änne`. German reviewer lists are the
 * reason this is not merely `toLowerCase()`.
 */
const fold = (value: string): string =>
    value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

/**
 * The index of the next option whose label starts with `prefix`, or `null`.
 *
 * The search starts AFTER `activeIndex` and wraps around once. Wrapping is what makes
 * repeated presses of the same letter cycle through all matches — the behaviour the
 * requester asked for on lists of eleven and more names. Note the contrast with
 * {@link resolveNextOptionIndex}, which must NOT wrap: arrow keys walk a list,
 * typeahead searches it.
 *
 * Disabled options never match. An empty prefix matches nothing, rather than
 * everything.
 */
export function findByPrefix(
    options: readonly SearchableOption[],
    prefix: string,
    activeIndex: number,
): number | null {
    const needle = fold(prefix);
    if (needle === '' || options.length === 0) {
        return null;
    }

    for (let step = 1; step <= options.length; step += 1) {
        const index = (activeIndex + step + options.length) % options.length;
        const option = options[index];
        if (option && option.disabled !== true && fold(option.label).startsWith(needle)) {
            return index;
        }
    }
    return null;
}
